import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { CircularProgress } from '@mui/material';
import { PillButton, sa } from '../../../components/superAdminUi';
import WizardShell from '../../../components/common/WizardShell';
import { loaderAction } from '../../../store/loaderStore';
import { schoolService, matGroupService } from '../../../services';
import customerTypeService from '../../../services/api/customerTypeService';
import { routesObj } from '../../../routes';
import SchoolDuplicateWatch from '../../../components/common/SchoolDuplicateWatch';
import { emptySchoolFormValues, fromGias, prefillFromPrevious } from '../../../utils/schoolFormMapper';
import { buildSchoolPayload } from './payload';
import SchoolDetailsStep from './SchoolDetailsStep';
import SubscriptionStep from './SubscriptionStep';
import ReviewStep from './ReviewStep';
import SummaryRail from './SummaryRail';

const STEPS = ['School details', 'Subscription & products', 'Review & create'];

const initialValues = {
  ...emptySchoolFormValues(),
  products: [],
  isTestAccount: false,
  IsMultiYear: true,
  SubscriptionTerm: 1,
  Year1Cost: 0,
  Year2Cost: 0,
  Year3Cost: 0,
  VatPercentApplied: 20,
  Discount: 0,
  PaymentCurrency: 1,
  PaymentMode: 1,
  StartDate: null,
  EndDate: null,
  RenewalDate: null,
  File: null,
  FileName: null,
};

// GIAS fills a field only when it is still empty, so an operator's edit is never trampled;
// `false` is a real answer for the three-state characteristics.
const applyGiasIfEmpty = (values, gias, setFieldValue) => {
  Object.keys(gias).forEach((k) => {
    const incoming = gias[k];
    const current = values[k];
    const currentEmpty = current === '' || current === null || current === undefined;
    if (!currentEmpty) return;
    if (incoming === false) {
      setFieldValue(k, false);
    } else if (incoming !== '' && incoming !== null && incoming !== undefined) {
      setFieldValue(k, incoming);
    }
  });
};

function RegisterSchool(props) {
  const [activeStep, setActiveStep] = useState(0);
  const [matId, setMatId] = useState(null);
  const [groupName, setGroupName] = useState('');
  const [customerTypes, setCustomerTypes] = useState([]);
  const [giasSchoolData, setGiasSchoolData] = useState(null);
  const [giasLoading, setGiasLoading] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [duplicate, setDuplicate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(props.location.search);
    const matIdParam = params.get('matId');
    if (matIdParam) {
      const parsed = parseInt(matIdParam, 10);
      setMatId(parsed);
      // Cheap best-effort fetch of the group name for the "Adding to …" banner.
      matGroupService.getMatById(parsed).then((response) => {
        const data = response?.data ?? response;
        if (data && (data.Name || data.name)) setGroupName(data.Name || data.name);
      }).catch(() => {});
    }
  }, [props.location.search]);

  useEffect(() => {
    customerTypeService.getAllActiveCustomerTypes().then((response) => {
      if (response.success && response.data) setCustomerTypes(response.data);
    }).catch(() => {});
  }, []);

  const searchGias = (formik) => {
    const urn = (formik.values.Urn || '').toString().trim();
    if (!urn) {
      toast.error('Please enter School URN');
      return;
    }
    const urnNumber = parseInt(urn, 10);
    if (isNaN(urnNumber) || urnNumber <= 0) {
      toast.error('Please enter a valid School URN (numeric value)');
      return;
    }
    setGiasLoading(true);
    schoolService.GetGIASSchoolsByURN(urnNumber).then((response) => {
      const schoolData = response?.data ?? (response?.success === undefined ? response : null);
      if (schoolData) {
        setGiasSchoolData(schoolData);
        setShowManual(true);
        applyGiasIfEmpty(formik.values, fromGias(schoolData), formik.setFieldValue);
        toast.success('School details loaded. You can review and edit them below.');
      } else {
        toast.error(response?.message || 'No school found for this URN');
        setGiasSchoolData(null);
      }
      setGiasLoading(false);
    }).catch((error) => {
      toast.error(error?.message || 'Error fetching school details from GIAS');
      setGiasSchoolData(null);
      setGiasLoading(false);
    });
  };

  const canLeaveStep = (formik, step) => {
    if (step === 0) {
      const name = (formik.values.Name || '').trim();
      const urn = (formik.values.Urn || '').toString().trim();
      if (!name) {
        toast.error('Please enter School Name');
        return false;
      }
      if (!urn || isNaN(parseInt(urn, 10)) || parseInt(urn, 10) <= 0) {
        toast.error('Please enter a valid School URN');
        return false;
      }
      if (duplicate && duplicate.isDuplicate) {
        toast.error('A school with this name or URN already exists.');
        return false;
      }
    }
    return true;
  };

  const handleCreate = (formik) => {
    if (duplicate && duplicate.isDuplicate) {
      toast.error('A school with this name or URN already exists.');
      return;
    }
    setSubmitting(true);
    const payload = buildSchoolPayload(formik.values, { matId });
    props.loaderActions.dataLoading(true);
    schoolService.addNewIndividualSchoolPost(payload).then(async (response) => {
      props.loaderActions.dataLoading(false);
      setSubmitting(false);
      if (response.success) {
        // Invoice generation is best-effort and never blocks the flow.
        if (payload.StartDate && response.data) {
          try {
            const schoolId =
              response.data?.data?.Id || response.data?.Id || response.data?.data?.id || response.data?.id;
            if (schoolId) {
              await schoolService.generateInvoiceForSubscription({
                SchoolId: schoolId,
                YearNumber: 1,
                RenewalDateOverride: payload.RenewalDate ? new Date(payload.RenewalDate).toISOString() : null,
              });
            }
          } catch (invoiceError) {
            // Swallow — invoice failure must not fail school creation.
          }
        }
        toast.success('School added successfully');
        setCreated(true);
      } else {
        toast.error(response.message || 'Failed to add school');
      }
    }).catch(() => {
      props.loaderActions.dataLoading(false);
      setSubmitting(false);
      toast.error('Error adding school');
    });
  };

  const primaryNav = () => {
    if (matId) {
      props.history.push(`/group-detail?matId=${matId}`);
    } else {
      props.history.push(routesObj.SchoolListing.path);
    }
  };

  const saveAnother = (formik) => {
    formik.setValues({ ...initialValues, ...prefillFromPrevious(formik.values) });
    setGiasSchoolData(null);
    setDuplicate(null);
    setShowManual(true);
    setCreated(false);
    setActiveStep(0);
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={() => {}}
      validateOnBlur={false}
      validateOnChange
      validationSchema={Yup.object().shape({
        Name: Yup.string().trim().max(255, 'School name must be at most 255 characters.').required('School name is required.'),
        Urn: Yup.string().trim().required('School URN is required.'),
      })}
    >
      {(formik) => {
        const finalAction = submitting
          ? <CircularProgress size={32} sx={{ color: sa.color.teal }} />
          : <PillButton type="button" $size="lg" onClick={() => handleCreate(formik)}>Create school</PillButton>;

        return (
          <div>
            <SchoolDuplicateWatch name={formik.values.Name} urn={formik.values.Urn} onResult={setDuplicate} />
            <WizardShell
              title="Register New School"
              steps={STEPS}
              activeStep={activeStep}
              onStepSelect={(index) => setActiveStep(index)}
              hideFooter={created}
              onBack={() => setActiveStep((s) => s - 1)}
              onNext={() => { if (canLeaveStep(formik, activeStep)) setActiveStep((s) => s + 1); }}
              finalAction={finalAction}
              summary={<SummaryRail values={formik.values} />}
            >
              {activeStep === 0 && (
                <SchoolDetailsStep
                  formik={formik}
                  matId={matId}
                  groupName={groupName}
                  customerTypes={customerTypes}
                  giasLoading={giasLoading}
                  giasSchoolData={giasSchoolData}
                  showManual={showManual}
                  duplicate={duplicate}
                  onSearch={() => searchGias(formik)}
                  onShowManual={() => setShowManual(true)}
                />
              )}
              {activeStep === 1 && <SubscriptionStep formik={formik} />}
              {activeStep === 2 && (
                <ReviewStep
                  values={formik.values}
                  matId={matId}
                  groupName={groupName}
                  created={created}
                  onPrimaryNav={primaryNav}
                  onSaveAnother={() => saveAnother(formik)}
                />
              )}
            </WizardShell>
          </div>
        );
      }}
    </Formik>
  );
}

const mapDispatchToProps = (dispatch) => ({
  loaderActions: bindActionCreators(loaderAction, dispatch),
});

export default connect(null, mapDispatchToProps)(withRouter(RegisterSchool));
